import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Check, Image, Plus, X, Trash, ArrowsClockwise, CaretUp, CaretDown } from '@phosphor-icons/react';
import axios from 'axios';
import { API, useSettings } from '../../App';

const Settings = () => {
  const { settings, setSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  const { register, handleSubmit, setValue, watch } = useForm();

  // Watch values
  const heroImages = watch('hero_slider_images') || [];
  const notificationEmails = watch('notification_emails') || [];

  useEffect(() => {
    if (settings) {
      Object.keys(settings).forEach(key => {
        setValue(key, settings[key]);
      });
      // Initialize hero slider if not present
      if (!settings.hero_slider_images) {
        setValue('hero_slider_images', [
          "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/z5loo6f5_WhatsApp%20Image%202026-03-17%20at%2016.07.58%20%284%29.jpeg",
          "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/o5auixqq_WhatsApp%20Image%202026-03-17%20at%2016.07.58.jpeg",
          "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/9kbot33g_WhatsApp%20Image%202026-03-17%20at%2016.07.58%20%282%29.jpeg",
          "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/7n240wla_WhatsApp%20Image%202026-03-17%20at%2016.07.58%20%283%29.jpeg",
          "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/3c27o0kf_WhatsApp%20Image%202026-03-17%20at%2016.07.58%20%281%29.jpeg"
        ]);
      }
    }
  }, [settings, setValue]);

  const handleImageUpload = async (e, field, index = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(field);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (index !== null) {
        // Update slider image
        const images = [...heroImages];
        images[index] = imageUrl;
        setValue('hero_slider_images', images);
      } else {
        setValue(field, imageUrl);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Échec du téléchargement');
    } finally {
      setUploading(null);
    }
  };

  const addSliderImage = () => {
    setValue('hero_slider_images', [...heroImages, '']);
  };

  const removeSliderImage = (index) => {
    const images = heroImages.filter((_, i) => i !== index);
    setValue('hero_slider_images', images);
  };

  const moveSliderImage = (index, direction) => {
    const images = [...heroImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;
    [images[index], images[newIndex]] = [images[newIndex], images[index]];
    setValue('hero_slider_images', images);
  };

  const updateSliderImageUrl = (index, url) => {
    const images = [...heroImages];
    images[index] = url;
    setValue('hero_slider_images', images);
  };

  const addEmail = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    const emails = [...(notificationEmails || []), newEmail];
    setValue('notification_emails', emails);
    setNewEmail('');
  };

  const removeEmail = (index) => {
    const emails = notificationEmails.filter((_, i) => i !== index);
    setValue('notification_emails', emails);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setSaved(false);

    try {
      const response = await axios.put(`${API}/admin/settings`, data, {
        withCredentials: true
      });
      setSettings(response.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const fonts = [
    'Playfair Display',
    'Outfit',
    'Inter',
    'Roboto',
    'Montserrat',
    'Lora',
    'Merriweather',
    'Open Sans',
    'Raleway',
    'Cormorant Garamond',
    'Libre Baskerville',
    'DM Sans'
  ];

  const tabs = [
    { id: 'general', label: 'Général' },
    { id: 'slider', label: 'Slider Hero' },
    { id: 'typography', label: 'Typographie' },
    { id: 'notifications', label: 'Notifications' }
  ];

  return (
    <div data-testid="admin-settings">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-white mb-2">Paramètres</h1>
        <p className="text-stone-400">Configurez l'apparence et les options du site</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm rounded-t transition-colors ${
              activeTab === tab.id
                ? 'bg-white/10 text-white'
                : 'text-stone-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 max-w-4xl">
        
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            <section className="admin-card p-6">
              <h2 className="font-display text-xl text-white mb-6">Informations générales</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Nom du site</label>
                  <input
                    type="text"
                    className="admin-input"
                    {...register('site_name')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Slogan</label>
                  <input
                    type="text"
                    className="admin-input"
                    {...register('tagline')}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Titre Hero (H1)</label>
                  <input
                    type="text"
                    className="admin-input"
                    {...register('hero_title')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Sous-titre Hero</label>
                  <input
                    type="text"
                    className="admin-input"
                    {...register('hero_subtitle')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Description Hero</label>
                  <textarea
                    rows={3}
                    className="admin-input resize-none"
                    {...register('hero_description')}
                  />
                </div>
              </div>
            </section>

            <section className="admin-card p-6">
              <h2 className="font-display text-xl text-white mb-6">Vitesse du slider</h2>
              <div>
                <label className="block text-sm text-stone-400 mb-2">
                  Intervalle entre les images (en secondes)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="admin-input w-32"
                  {...register('slider_interval', { valueAsNumber: true })}
                  defaultValue={3}
                />
                <p className="text-xs text-stone-500 mt-2">
                  Par défaut : 3 secondes
                </p>
              </div>
            </section>
          </>
        )}

        {/* Slider Tab */}
        {activeTab === 'slider' && (
          <section className="admin-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-xl text-white">Images du Slider Hero</h2>
                <p className="text-sm text-stone-400 mt-1">
                  Gérez les images qui défilent dans la section hero de la page d'accueil
                </p>
              </div>
              <button
                type="button"
                onClick={addSliderImage}
                className="btn-secondary flex items-center gap-2"
              >
                <Plus size={16} weight="bold" />
                Ajouter
              </button>
            </div>

            <div className="space-y-4">
              {heroImages.map((image, index) => (
                <div key={index} className="flex gap-4 items-start p-4 bg-[#0A0A0A] rounded-lg">
                  {/* Preview */}
                  <div className="w-32 h-20 flex-shrink-0 overflow-hidden rounded bg-black">
                    {image ? (
                      <img src={image} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-600">
                        <Image size={24} />
                      </div>
                    )}
                  </div>

                  {/* URL Input */}
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={image}
                        onChange={(e) => updateSliderImageUrl(index, e.target.value)}
                        placeholder="URL de l'image"
                        className="admin-input flex-1"
                      />
                      <label className="btn-secondary cursor-pointer flex items-center gap-2 text-sm">
                        <Image size={16} />
                        {uploading === `slider_${index}` ? '...' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, `slider_${index}`, index)}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-stone-500">Image {index + 1}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveSliderImage(index, 'up')}
                      disabled={index === 0}
                      className="p-1.5 text-stone-400 hover:text-white disabled:opacity-30"
                    >
                      <CaretUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSliderImage(index, 'down')}
                      disabled={index === heroImages.length - 1}
                      className="p-1.5 text-stone-400 hover:text-white disabled:opacity-30"
                    >
                      <CaretDown size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSliderImage(index)}
                      className="p-1.5 text-stone-400 hover:text-red-400"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {heroImages.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  Aucune image dans le slider. Cliquez sur "Ajouter" pour commencer.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Typography Tab */}
        {activeTab === 'typography' && (
          <section className="admin-card p-6">
            <h2 className="font-display text-xl text-white mb-6">Typographie</h2>
            <p className="text-sm text-stone-400 mb-6">
              Personnalisez les polices utilisées sur le site
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm text-stone-400 mb-2">Police des titres</label>
                <select className="admin-input" {...register('primary_font')}>
                  {fonts.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
                <p className="text-xs text-stone-500 mt-2">
                  Utilisée pour les titres (H1, H2, H3...)
                </p>
              </div>
              <div>
                <label className="block text-sm text-stone-400 mb-2">Police du corps</label>
                <select className="admin-input" {...register('secondary_font')}>
                  {fonts.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
                <p className="text-xs text-stone-500 mt-2">
                  Utilisée pour le texte courant
                </p>
              </div>
            </div>

            {/* Preview */}
            <div className="p-6 bg-black rounded-lg border border-white/10">
              <h3 className="text-lg text-white mb-4">Aperçu</h3>
              <div 
                className="text-3xl text-white mb-4"
                style={{ fontFamily: watch('primary_font') || 'Playfair Display' }}
              >
                Titre avec {watch('primary_font') || 'Playfair Display'}
              </div>
              <p 
                className="text-white/60"
                style={{ fontFamily: watch('secondary_font') || 'Outfit' }}
              >
                Ceci est un paragraphe d'exemple avec la police {watch('secondary_font') || 'Outfit'}. 
                Elle sera utilisée pour tout le texte courant du site.
              </p>
            </div>
          </section>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <section className="admin-card p-6">
            <h2 className="font-display text-xl text-white mb-6">Notifications Email</h2>
            <p className="text-sm text-stone-400 mb-6">
              Les demandes de contact seront envoyées à ces adresses email 
              (configuration SendGrid requise pour l'envoi)
            </p>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  className="admin-input flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                />
                <button type="button" onClick={addEmail} className="btn-secondary">
                  <Plus size={18} weight="bold" />
                </button>
              </div>

              {notificationEmails?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {notificationEmails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#121212] text-stone-300 text-sm rounded"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(index)}
                        className="text-stone-500 hover:text-red-400"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Save Button - Always visible */}
        <div className="flex items-center gap-4 sticky bottom-0 bg-[#050505] py-4 border-t border-white/10">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Check size={18} weight="bold" />
                Sauvegarder
              </>
            )}
          </button>
          {saved && (
            <span className="text-green-400 text-sm">Paramètres sauvegardés !</span>
          )}
        </div>
      </form>
    </div>
  );
};

export default Settings;
